<template>
  <TornadoChart
    :x-unit="chartingData?.bhp.targetXUnit"
    :y-unit="chartingData?.bhp.targetYUnit"
    computeDomainMinY
    :computedDomainYBufferMultiplier="0.1"
    reverse-y
    x-label="In Situ Flow Rate"
    y-label="Required Brake HP"
    :frequency-data-sets="chartingData?.bhp.operatingDataSets || []"
    :ror-data-sets="chartingData?.bhp.rorDataSets || []"
    :additional-data-sets="
      chartingData ? [chartingData.bhp.analysisDataSet] : []
    "
    :operating-points="chartingData?.bhp.operatingPoints"
    :x-legend-label="xLegendLabel"
    :y-legend-label="yLegendLabel"
  />
</template>

<script setup lang="ts">
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario } from "@/composables/forms/formTypes";
import TornadoChart from "./TornadoChart.vue";
import { usePumpChartingData } from "@/libs/charting";
import type { PumpTornadoChartViewData } from "@/libs/charting";
import { computed } from "vue";
import {
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  minFrequency: number;
  maxFrequency: number;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  xLegendLabel?: string;
  yLegendLabel?: string;
  pumpTornadoChartData?: PumpTornadoChartViewData;
}>();

const _scenario = props.scenario ?? injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const _scenarioCalculations =
  props.scenarioCalculations ?? injectStrict(scenarioCalculationsKey);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(_scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const chartingData = computed(() => {
  if (!props.pumpTornadoChartData) {
    return usePumpChartingData(
      props.minFrequency,
      props.maxFrequency,
      _scenarioCalculations,
      targetLiquidFlowUnit.value,
      targetPowerUnit.value,
      targetLargeLengthUnit.value,
      targetPressureUnit.value
    );
  } else {
    return props.pumpTornadoChartData;
  }
});
</script>
